import React, { useState } from 'react';
import { Car, Users, Crown, MapPin, Calendar, Send, Loader2, CheckCircle, Shield, Clock, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAR_TYPES = [
  { name: 'Economy', desc: 'Perfect for city travel and short trips', price: 'From Rs.1,500/day', icon: <Car className="w-8 h-8" />, features: ['AC', '4 Seater', 'Fuel included'] },
  { name: 'SUV', desc: 'Spacious and comfortable for families', price: 'From Rs.3,000/day', icon: <Car className="w-8 h-8" />, features: ['AC', '7 Seater', 'Luggage space'] },
  { name: 'Luxury', desc: 'Premium vehicles for special occasions', price: 'From Rs.8,000/day', icon: <Crown className="w-8 h-8" />, features: ['Premium AC', 'Leather seats', 'WiFi'] },
  { name: 'Chauffeur', desc: 'Professional driver included for hassle-free travel', price: 'From Rs.4,000/day', icon: <Users className="w-8 h-8" />, features: ['Expert driver', 'Door-to-door', 'Flexible timing'] },
];

const BENEFITS = [
  { icon: <Shield className="w-6 h-6" />, title: 'Fully Insured', desc: 'All vehicles come with comprehensive insurance coverage' },
  { icon: <Clock className="w-6 h-6" />, title: '24/7 Support', desc: 'Round-the-clock roadside assistance and customer support' },
  { icon: <CreditCard className="w-6 h-6" />, title: 'Best Rates', desc: 'Competitive pricing with no hidden charges' },
  { icon: <MapPin className="w-6 h-6" />, title: 'Flexible Pickup', desc: 'Airport, hotel, or any location pickup available' },
];

export default function CarRentalPage() {
  const [form, setForm] = useState({ name: '', phone: '', destination: '', travel_date: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/service-inquiry`, { ...form, service_type: 'car-rental' });
      setSuccess(true);
      setForm({ name: '', phone: '', destination: '', travel_date: '', message: '' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div data-testid="car-rental-page" className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src="https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Car Rental" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Travel in Style</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">Car Rentals Worldwide</h1>
          <p className="font-body text-base text-white/80 max-w-xl">Self-drive or chauffeur-driven, we have the perfect ride for your journey</p>
        </div>
      </div>

      {/* Car Types */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Our Fleet</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">Choose Your Ride</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAR_TYPES.map(car => (
              <div key={car.name} data-testid={`car-type-${car.name.toLowerCase()}`} className="p-6 rounded-3xl bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-2 transition-transform duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-2xl flex items-center justify-center mb-4 text-gold">{car.icon}</div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-1">{car.name}</h3>
                <p className="font-body text-xs text-gray-500 mb-3">{car.desc}</p>
                <p className="font-heading text-base font-bold text-gold mb-4">{car.price}</p>
                <div className="space-y-2">
                  {car.features.map(f => (
                    <div key={f} className="flex items-center gap-2 font-body text-xs text-gray-500">
                      <CheckCircle className="w-3 h-3 text-green-500" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="section-padding bg-navy-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">Why Rent With Us</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map(b => (
              <div key={b.title} className="text-center p-6 rounded-3xl bg-white border border-gray-100">
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-gold">{b.icon}</div>
                <h4 className="font-heading text-base font-semibold text-navy mb-2">{b.title}</h4>
                <p className="font-body text-xs text-gray-500">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <div className="section-padding bg-navy">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Reserve Now</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Book Your Ride</h2>
          </div>
          {success ? (
            <div data-testid="car-form-success" className="text-center p-10 rounded-3xl bg-white/10 border border-white/20">
              <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Booking Request Sent!</h3>
              <p className="font-body text-sm text-gray-400">We'll confirm your vehicle within a few hours.</p>
              <button onClick={() => setSuccess(false)} className="mt-4 font-body text-sm text-gold hover:underline">Book another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="car-rental-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  data-testid="car-name" placeholder="Full Name"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  data-testid="car-phone" placeholder="Phone Number"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" value={form.destination} onChange={e => setForm({...form, destination: e.target.value})}
                  data-testid="car-destination" placeholder="Pickup Location / City"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="date" value={form.travel_date} onChange={e => setForm({...form, travel_date: e.target.value})}
                  data-testid="car-date"
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                data-testid="car-message" placeholder="Car type preference, duration, special requests..."
                className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold resize-none" />
              <button type="submit" disabled={loading} data-testid="car-submit"
                className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Book Your Ride</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
